/**
 * emailDashboardMockAdapter
 *
 * P3 shell-phase mock data source for the War Room Email Dashboard.
 *
 * This is the swappable boundary: once P1's Email read model exists, this
 * file (or a thin branch inside useEmailDashboardData) is the only place
 * that needs to change to call a real API. No consuming component should
 * import this file directly — always go through useEmailDashboardData.
 *
 * Checkpoint 3.1: the Overview KPI set was replaced. MissionAssist Email
 * exists to drive Email -> Landing Page -> Quote -> High Intent ->
 * Application -> Revenue, so the primary KPI row is acquisition-funnel
 * metrics rather than provider engagement metrics (open/click rate). Those
 * engagement metrics belong to the Performance/Creative views (not yet
 * built), not Overview.
 *
 * Checkpoint 3.3: Executive Summary and the Acquisition Funnel were
 * showing the same five raw counts twice. Executive Summary's job is now
 * "how efficiently is the program performing" (rates), while the Funnel's
 * job is "how many people progressed through each stage" (raw counts) —
 * both still derive from one set of raw counts computed once here, so
 * there is exactly one source of truth: kpis[] holds derived rates,
 * funnel[] holds the raw progression, and swapping either for a live read
 * model still only requires touching this file.
 *
 * kpis[].status / funnel[].status follow the existing Metric Confidence
 * Standard (exact | estimated | partial_data | missing_data |
 * not_applicable), same as useWarRoomMetrics.
 *
 * DASH-EMAIL-009: processingHealth is no longer built by this file at all
 * (see useEmailDashboardData.js) -- it is real, CHECKPOINT-grain data from
 * GET /api/email-delivery/dashboard/processing-health, using a distinct,
 * operational health vocabulary (healthy | failed | unknown, plus the
 * still-unused legacy watch/critical states EmailHealthCard.jsx's
 * STATE_CONFIG retains) rather than the Metric Confidence Standard above —
 * it describes system/pipeline state, not data confidence, so reusing the
 * Metric Confidence vocabulary there would be semantically wrong
 * ("processing status: estimated" doesn't mean anything).
 *
 * Checkpoint 3.4E: Overview gained Program/Campaign/Broadcast filtering,
 * unified with Performance's.
 *
 * Checkpoint 3.4E remediation (adversarial-review finding): the initial
 * 3.4E implementation kept the pre-existing, separately-tuned BASE_VOLUME/
 * RECENT_ACTIVITY_BASE constants for the DEFAULT (all/all/all) case and
 * only routed a non-default filter selection through the shared
 * BROADCAST_ROWS_BASE catalog. Those two "universes" were never
 * calibrated to be consistent with each other (BASE_VOLUME.delivered was
 * 5,000; the catalog's own total was 15,407) — so narrowing from "All
 * Programs" to a specific Program could show MORE volume than "All," which
 * is logically invalid for an additive metric under a subset filter.
 *
 * Fixed by retiring BASE_VOLUME/RECENT_ACTIVITY_BASE/this file's own
 * ENVIRONMENT_MULTIPLIER entirely.
 *
 * DASH-EMAIL-008B: this module no longer imports or computes anything from
 * the shared broadcast catalog (emailPerformanceMockAdapter.js's
 * BROADCAST_ROWS_BASE/filterBroadcastRows/scaledValue) at all. That
 * catalog-derived data was the source of every remaining synthetic number
 * on Overview -- the three "estimated" rate KPIs and every Recent Activity
 * row -- and this checkpoint retires all of it in favor of an honest
 * UNAVAILABLE/empty presentation (see buildKpis below; Recent Activity
 * itself was later replaced entirely by real data -- see
 * RECENT-BROADCAST-PERFORMANCE-001's own comment further below).
 * Overview's only real numeric fact (Delivered) comes exclusively from
 * useEmailDashboardData.js's live GET /api/email-delivery/dashboard/summary
 * fetch, never from this file. Performance's OWN mock adapter
 * (emailPerformanceMockAdapter.js) still owns and uses this catalog
 * independently for its own, separately-scoped, untouched view.
 */

// DASH-EMAIL-P1-HIGHINTENT-001 Slice 4: "processed" leads the funnel
// (real EDT MESSAGE-grain data, overlaid by useEmailDashboardData.js
// exactly like "delivered" already is); "applications_submitted"/
// "inforce_policies" trail High Intent Actions as PERMANENT placeholders
// -- no backend source exists for either, so unlike every other stage
// here, useEmailDashboardData.js never overlays a real value onto them.
// They stay at this function's own default `value: null, status:
// "missing_data"` forever, with an explicit `reason` (see below) so the
// existing ConfidenceIndicator tooltip discloses WHY, matching this same
// file's own established stage-shape/reason convention.
const FUNNEL_ORDER = [
  "processed",
  "delivered",
  "landing_page_views",
  "quote_starts",
  "quote_completes",
  "high_intent_actions",
  "applications_submitted",
  "inforce_policies",
];
const FUNNEL_LABELS = {
  processed: "Processed",
  delivered: "Delivered",
  landing_page_views: "Landing Page Views",
  quote_starts: "Quote Starts",
  quote_completes: "Quote Completes",
  high_intent_actions: "High Intent Actions",
  applications_submitted: "Applications Submitted",
  inforce_policies: "Inforce Policies",
};

const TRACKING_NOT_CONNECTED_REASON = "Tracking not connected.";

// Only these two stages ever carry this reason -- both are permanent,
// backend-less placeholders (see FUNNEL_ORDER's own comment above), never
// a genuinely-sourced stage that merely failed to load this time (those
// stay reason-less, matching this file's own pre-existing convention).
const FUNNEL_STAGE_REASONS = {
  applications_submitted: TRACKING_NOT_CONNECTED_REASON,
  inforce_policies: TRACKING_NOT_CONNECTED_REASON,
};

// DASH-EMAIL-010: emails_sent (Attempted Sends) previously fabricated a
// value as delivered * 1.04 — a synthetic, production-looking number
// explicitly rejected by EMAIL-ANALYTICS-SPEC-001 §13.1/§25 (Attempted
// Sends is UNAVAILABLE at Broadcast+ grain until a real send-attempt fact
// exists). No replacement estimate is substituted; the metric now reports
// UNAVAILABLE honestly. Delivery Rate's denominator was exactly that
// fabricated value, so it is UNAVAILABLE for the same reason — per the
// Count/Rate Availability Invariant (§11), a rate may never be presented
// merely because its numerator (Delivered) is available.
const UNAVAILABLE_REASON =
  "Attempted Sends is not yet backed by an authoritative production fact — no synthetic estimate is substituted (EMAIL-ANALYTICS-SPEC-001 §12, §13.1).";
const DELIVERY_RATE_UNAVAILABLE_REASON =
  "Delivery Rate's denominator (Attempted Sends) is unavailable, so the rate cannot be presented (EMAIL-ANALYTICS-SPEC-001 §11).";

// DASH-EMAIL-008B: landing_page_view_rate/quote_completion_rate/
// high_intent_rate previously computed a real-looking synthetic percentage
// (numerator and denominator both drawn from the retired mock broadcast
// catalog) with status: "estimated" -- a disclosed-but-fake number is still
// not acceptable once this checkpoint is done. Each numerator (landing
// page views, quote starts/completes, high intent actions) has no
// authoritative source fact anywhere in this repository, exactly like
// Attempted Sends above -- so each rate is now explicitly UNAVAILABLE via
// the SAME availability mechanism, never a numeric value dressed up with a
// confidence badge. After this checkpoint every one of Overview's five KPI
// cards is either real (none yet, at this mock-adapter layer) or
// explicitly unavailable -- none synthetic-but-labeled.
const LANDING_PAGE_VIEW_RATE_UNAVAILABLE_REASON =
  "Landing Page View Rate requires a landing-page-view fact that does not exist yet — no synthetic estimate is substituted.";
const QUOTE_COMPLETION_RATE_UNAVAILABLE_REASON =
  "Quote Completion Rate requires quote-start/quote-complete facts that do not exist yet — no synthetic estimate is substituted.";
const HIGH_INTENT_RATE_UNAVAILABLE_REASON =
  "High Intent Rate requires a high-intent-action fact that does not exist yet — no synthetic estimate is substituted.";

function buildKpis() {
  return {
    emails_sent: { value: undefined, availability: "UNAVAILABLE", reason: UNAVAILABLE_REASON },
    delivery_rate: { value: undefined, availability: "UNAVAILABLE", reason: DELIVERY_RATE_UNAVAILABLE_REASON },
    landing_page_view_rate: {
      value: undefined,
      availability: "UNAVAILABLE",
      reason: LANDING_PAGE_VIEW_RATE_UNAVAILABLE_REASON,
    },
    quote_completion_rate: {
      value: undefined,
      availability: "UNAVAILABLE",
      reason: QUOTE_COMPLETION_RATE_UNAVAILABLE_REASON,
    },
    high_intent_rate: {
      value: undefined,
      availability: "UNAVAILABLE",
      reason: HIGH_INTENT_RATE_UNAVAILABLE_REASON,
    },
  };
}

// DASH-EMAIL-008: the funnel used to show all five stages as synthetic
// "estimated" counts, including Delivered -- a real, MESSAGE-grain fact
// that must come exclusively from email_delivery_messages (via
// GET /api/email-delivery/dashboard/summary), never from this mock
// catalog. This function no longer supplies a numeric value for ANY
// stage, including Delivered: useEmailDashboardData.js is now the ONLY
// place that ever injects a real Delivered value (see that hook's own
// doc comment) -- there is no mock fallback left for it to leak through,
// by construction, not by a runtime check. Landing Page Views/Quote
// Starts/Quote Completes/High Intent Actions remain genuinely unavailable
// -- no source system for any of them exists anywhere in this
// repository, and none is invented here (see this checkpoint's
// characterization report).
function buildFunnel() {
  return FUNNEL_ORDER.map((key) => ({
    label: FUNNEL_LABELS[key],
    value: null,
    // "missing_data" -- the existing Metric Confidence Standard vocabulary
    // (exact | estimated | partial_data | missing_data | not_applicable,
    // see this file's header comment) already has a value for exactly
    // this case; never invent a new status string outside that enum.
    status: "missing_data",
    reason: FUNNEL_STAGE_REASONS[key],
  }));
}

// DASH-EMAIL-009: processingHealth is no longer built here at all. It is
// now real, CHECKPOINT-grain data from
// GET /api/email-delivery/dashboard/processing-health (see
// email_delivery_processing_health_service.py's own canonical-source
// docstring) -- useEmailDashboardData.js is the ONLY place that ever
// injects a processingHealth value into Overview's returned data (same
// "retire the mock, replace with the real hook-level fetch" pattern
// DASH-EMAIL-008 already established for the funnel's Delivered stage).
// There is no mock fallback left for it to leak through, by construction:
// getMockEmailDashboardData's own return object below no longer has a
// processingHealth key at all.

// RECENT-BROADCAST-PERFORMANCE-001: Recent Activity previously rendered
// real-looking synthetic per-broadcast rows derived from the retired mock
// catalog, with zero confidence disclosure. It is now "Recent Broadcast
// Performance", real, canonical-Broadcast-attributed data from
// GET /api/email-delivery/dashboard/recent-broadcast-performance (see
// useEmailDashboardData.js's own fetchRealRecentBroadcastPerformance) --
// there is no mock builder for it here at all anymore, by construction
// (getMockEmailDashboardData's own return object below no longer has a
// recentActivity key), matching the same "retire the mock, replace with
// the real hook-level fetch" pattern DASH-EMAIL-008/DASH-EMAIL-009 already
// established for Delivered/Processing Health.

/**
 * getMockEmailDashboardData
 *
 * @param {Object} params
 * @param {'all'|'qualification'|'production'} params.environment
 * @param {string} params.period - a named preset ("7d"|"30d"|"mtd"|"ytd") or "custom".
 * @param {{startDate:string,endDate:string}|null} [params.customRange] - only meaningful when period === "custom".
 * @param {string} [params.program] - Program id, or "all" / omitted for All Programs.
 * @param {string} [params.campaign] - Campaign id, or "all" / omitted for All Campaigns.
 * @param {string} [params.broadcast] - Broadcast id, or "all" / omitted for All Broadcasts.
 * @returns {{
 *   kpis: Record<string, {value:number|undefined, status?:string, availability?:string, reason:string}>,
 *   funnel: Array<{label:string, value:number|null, status:string}>,
 *   environment: string,
 *   period: string,
 *   customRange: {startDate:string,endDate:string}|null,
 *   filters: {program:string, campaign:string, broadcast:string},
 * }}
 *
 * DASH-EMAIL-008B: environment/period/program/campaign/broadcast are all
 * still accepted and echoed back in `filters`/`environment`/`period`/
 * `customRange` (contract-complete, and EmailFilterBar's shared draft/apply
 * plumbing still calls this the same way it always has), but NONE of them
 * drive any returned number anymore — every kpis entry and the funnel are
 * now identical regardless of which value is passed. This is intentional
 * and honest: Overview's only real facts are account-wide/real-filtered by
 * construction (see useEmailDashboardData.js), and every other Overview
 * number has been retired to an explicit unavailable/empty state rather
 * than continuing to visibly (but falsely) respond to these controls.
 */
export function getMockEmailDashboardData({
  environment = "all",
  period = "mtd",
  customRange = null,
  program = "all",
  campaign = "all",
  broadcast = "all",
} = {}) {
  return {
    kpis: buildKpis(),
    funnel: buildFunnel(),
    environment,
    period,
    customRange: period === "custom" ? customRange : null,
    filters: { program, campaign, broadcast },
  };
}

export default getMockEmailDashboardData;
